import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import { Link } from 'lego-on-react';
import * as React from 'react';
import { memo, MutableRefObject, useCallback, useRef, useState } from 'react';

import { getPropsByStatus, mapIconIframe } from '../../mappers/brandlift/brandlift';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { Bem } from '../../service/Bem/Bem';
import { formatNumber } from '../../service/FormatHelpers/FormatHelpers';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BrandSurveyStatus, BrandSurveyStopReason, SurveyStatus } from '../../typings/campaigns';
import { LinkTarget, TranslateBrandLift } from '../../typings/common';
import { CurrencyCode } from '../../typings/graphql-grid';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ModalDecorator } from '../ModalDecorator/ModalDecorator';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';
import { Tab, TabList, TabPanel, Tabs, TabsSize } from '../Tabs/Tabs';

import './BrandLiftIFrame.css';
import * as keyset from './BrandLiftIFrame.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);
const UNFEASIBLE_REASONS_MAX_COUNT = 2;

export interface BrandLiftIFrameProps {
    isVisible?: boolean;
    clientId: string;
    brandSurveyStatus: BrandSurveyStatus;
    brandSurveyId: string;
    surveysUiUrl: string;
    rejectReasonsText: string[];
    currency: CurrencyCode;
    budgetThreshold: number;
    campaignId: string;
    onClose: () => void;
    getRejectReasonText: (reasonsIds: string[], brandSurveyId: string) => void;
    onStopSurvey: (campaignIds: string[]) => void;
}

enum BrandLiftCanvasMessage {
    save = 'save'
}

const BLOCK = 'brand-lift-iframe';

const getSrc = (clientId: string, brandSurveyId: string, surveysUiUrl: string): [string, string] => {
    const iframeSrc = UrlHelpers.getCanvasBrandLiftPreviewUrl(clientId, brandSurveyId, surveysUiUrl);
    const iframeStatisticSrc = UrlHelpers.getCanvasBrandLiftPreviewStatisticUrl(clientId, brandSurveyId, surveysUiUrl);

    return [iframeSrc, iframeStatisticSrc];
};

const useMessageIframe = (isVisible: boolean): [MutableRefObject<HTMLIFrameElement | null>, () => void] => {
    const modal = useRef<HTMLIFrameElement>(null);

    const onMessage = (contentWindow: WindowProxy, type: BrandLiftCanvasMessage) => {
        contentWindow.postMessage({ type }, '*');
    };

    const messageDecorator = (type: BrandLiftCanvasMessage) => () => {
        if (!isVisible || !modal || !modal.current || !modal.current.contentWindow) {
            return;
        }

        onMessage(modal.current.contentWindow, type);
    };

    const onSaveMessage = messageDecorator(BrandLiftCanvasMessage.save);

    return [modal, onSaveMessage];
};

const getHeader = (
    onClose: () => void,
    brandSurveyStatus: BrandSurveyStatus,
    currency: CurrencyCode,
    budgetThreshold: number,
    rejectReasonsText: string[]
) => {
    const { surveyStatus } = brandSurveyStatus;
    const { icon, tone } = mapIconIframe(surveyStatus);
    const { title, text } = getStatusTextIframe(brandSurveyStatus, currency, budgetThreshold, rejectReasonsText);
    const propertyByStatus = getPropsByStatus(surveyStatus);

    return (
        <div className={Bem.cls(BLOCK, 'header')}>
            <div className={Bem.cls(BLOCK, 'header-title')}>
                <div className={Bem.cls(BLOCK, 'header-logo')}>
                    <Shape2 icon={Shape2Icon.vision} width={28} height={28} />
                    <Shape2 icon={Shape2Icon.visionRuText} width={130} height={30} />
                </div>
                <div className={Bem.cls(BLOCK, 'header-text')}>{i18n('brandlift')}</div>
                <Link cls={Bem.cls(BLOCK, 'close')} theme="pseudo" onClick={onClose}>
                    <Shape2 icon={Shape2Icon.close} width={15} height={15} />
                </Link>
            </div>
            <div
                className={Bem.cls({
                    block: BLOCK,
                    elem: 'notification-header',
                    mods: { [propertyByStatus]: true }
                })}
            >
                <Shape2
                    cls={Bem.cls(BLOCK, 'notification-icon')}
                    icon={icon}
                    tone={tone}
                    hoverable={true}
                    width={18}
                    height={18}
                />
                <span className={Bem.cls(BLOCK, 'notification-title')}>{title}</span>
                <span className={Bem.cls(BLOCK, 'notification-text')}>{text}</span>
            </div>
        </div>
    );
};

const getBody = (
    modal: MutableRefObject<HTMLIFrameElement | null>,
    iframeSrc: string,
    iframeStatisticSrc: string,
    surveyStatus: SurveyStatus
) => {
    const [selectIndex, setSelect] = useState(0);

    switch (surveyStatus) {
        case SurveyStatus.ACTIVE:
        case SurveyStatus.COMPLETED:
            return (
                <div className={Bem.cls(BLOCK, 'body')}>
                    <Tabs
                        size={TabsSize.m}
                        selectedIndex={selectIndex}
                        onSelect={setSelect}
                        selectedTabPanelClassName={Bem.cls(BLOCK, 'tab-panel-selected')}
                    >
                        <TabList className={Bem.cls(BLOCK, 'tab-list')}>
                            <Tab>{i18n('params')}</Tab>
                            <Tab>{i18n('result')}</Tab>
                        </TabList>
                        <TabPanel className={Bem.cls(BLOCK, 'tab-panel')}>
                            <iframe ref={modal} className={Bem.cls(BLOCK, 'iframe')} frameBorder="0" src={iframeSrc} />
                        </TabPanel>
                        <TabPanel className={Bem.cls(BLOCK, 'tab-panel')}>
                            <iframe className={Bem.cls(BLOCK, 'iframe')} frameBorder="0" src={iframeStatisticSrc} />
                        </TabPanel>
                    </Tabs>
                </div>
            );
        default:
            return (
                <div className={Bem.cls(BLOCK, 'body')}>
                    <iframe ref={modal} className={Bem.cls(BLOCK, 'iframe')} frameBorder="0" src={iframeSrc} />
                </div>
            );
    }
};

const getFooter = (
    surveyStatus: SurveyStatus,
    onCancel: () => void,
    onSave: () => void,
    onStopSurvey: (campaignIds: string[]) => void,
    campaignId: string
) => {
    const isDraft = surveyStatus === SurveyStatus.DRAFT;
    const showSaveButton = surveyStatus === SurveyStatus.DRAFT || surveyStatus === SurveyStatus.MODERATION_REJECTED;

    return (
        <div className={Bem.cls(BLOCK, 'footer')}>
            {showSaveButton && (
                <ButtonDecorator theme="action" size="n" cls={Bem.cls(BLOCK, 'save-btn')} onClick={onSave}>
                    {i18n('save')}
                </ButtonDecorator>
            )}

            <ButtonDecorator
                theme={showSaveButton ? 'normal' : 'action'}
                size="n"
                cls={Bem.cls(BLOCK, 'cancel-btn')}
                onClick={onCancel}
            >
                {showSaveButton ? i18n('cancel') : i18n('close')}
            </ButtonDecorator>
            {isDraft && (
                <ButtonDecorator
                    theme="normal"
                    size="n"
                    cls={Bem.cls(BLOCK, 'stop-btn')}
                    onClick={() => campaignId && onStopSurvey([campaignId])}
                >
                    {i18n('stop-research')}
                </ButtonDecorator>
            )}
        </div>
    );
};

const getStatusTextIframe = (
    brandSurveyStatus: BrandSurveyStatus,
    currency: CurrencyCode,
    budgetThreshold: number,
    rejectReasonsText: string[]
): TranslateBrandLift => {
    const { surveyStatus, brandSurveyStopReasons } = brandSurveyStatus;

    const mapper: Record<SurveyStatus, TranslateBrandLift> = {
        [SurveyStatus.ACTIVE]: {
            title: i18n('active'),
            text: i18n('active-text')
        },
        [SurveyStatus.COMPLETED]: {
            title: i18n('completed')
        },
        [SurveyStatus.MODERATION_REJECTED]: {
            title: i18n('reject'),
            text: ` ${rejectReasonsText && rejectReasonsText.join('. ')}`
        },
        [SurveyStatus.DRAFT]: {
            title: i18n('draft'),
            text: i18n('draft-text')
        },
        [SurveyStatus.MODERATION]: {
            title: i18n('moderation'),
            text: i18n('moderation-text')
        },
        [SurveyStatus.UNFEASIBLE]: {
            title: i18n('stop'),
            text: getUnfeasibleReasonForTextIframe(brandSurveyStopReasons, currency, budgetThreshold)
        }
    };

    return mapper[surveyStatus];
};

const getUnfeasibleReasonForTextIframe = (
    brandSurveyStopReasons: BrandSurveyStopReason[],
    currency: CurrencyCode,
    budgetThreshold: number
): React.ReactNode => {
    if (brandSurveyStopReasons.length === 0) {
        return undefined;
    }

    const budgetThresholdText = formatNumber(budgetThreshold);
    const currencySymbol = mapStringToCurrencySymbol(currency);
    const mapper: Record<BrandSurveyStopReason, string> = {
        [BrandSurveyStopReason.LOW_BUDGET]: i18n('stop-budget', {
            currencySymbol,
            budgetThreshold: budgetThresholdText
        }),
        [BrandSurveyStopReason.LOW_REACH]: i18n('stop-coverage')
    };

    if (brandSurveyStopReasons.length === UNFEASIBLE_REASONS_MAX_COUNT) {
        return i18nRaw('stop-budget-and-coverage', {
            moreInfoLink: (
                <StyledLink
                    cls={Bem.cls(BLOCK, 'more-info-link')}
                    theme={StyledLinkTheme.white}
                    target={LinkTarget.blank}
                    href={UrlHelpers.getCommonHelpUrl('direct/brand-lift.html', UrlHelpers.getTld())}
                >
                    {i18n('more-info-link')}
                </StyledLink>
            )
        });
    }

    const [reason] = brandSurveyStopReasons;

    return mapper[reason];
};

export const BrandLiftIFrame = memo(
    ({
        isVisible,
        onClose,
        clientId,
        brandSurveyStatus,
        brandSurveyId,
        surveysUiUrl,
        currency,
        budgetThreshold,
        rejectReasonsText,
        getRejectReasonText,
        onStopSurvey,
        campaignId
    }: BrandLiftIFrameProps) => {
        const { surveyStatus } = brandSurveyStatus;
        const getIframeSrc = useCallback(getSrc, [clientId, brandSurveyId, surveysUiUrl]);
        const [iframeSrc, iframeStatisticSrc] = getIframeSrc(clientId, brandSurveyId, surveysUiUrl);

        const [modal, onSaveMessage] = useMessageIframe(Boolean(isVisible));

        if (brandSurveyStatus.reasonIds.length && !rejectReasonsText) {
            getRejectReasonText(brandSurveyStatus.reasonIds, brandSurveyId);
        }

        return (
            <ModalDecorator
                visible={Boolean(isVisible)}
                theme={'clear'}
                cls={Bem.cls(BLOCK, 'modal')}
                onOutsideClick={onClose}
                bodyCls={Bem.cls(BLOCK, 'modal-body')}
            >
                {getHeader(onClose, brandSurveyStatus, currency, budgetThreshold, rejectReasonsText)}
                {getBody(modal, iframeSrc, iframeStatisticSrc, surveyStatus)}
                {getFooter(surveyStatus, onClose, onSaveMessage, onStopSurvey, campaignId)}
            </ModalDecorator>
        );
    }
);
